import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Currency, LoanStatusExternal } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { MerchantScoreCard } from '@/components/modules/Metrics/shared/components/TopMerchants/components/MerchantScoreCard';
import { MerchantMetricAnalyzer } from '@/components/modules/Metrics/shared/components/TopMerchants/entities/MerchantMetricAnalyzer';
import { useMetricMerchantViews } from '@/components/modules/Metrics/shared/components/TopMerchants/hooks/useMetricMerchantViews';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { PendingDataType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface TopMerchantsProps {
  timeInterval: TimeInterval;
}
const currency = Currency.EUR;
const loanStatusExternal = [LoanStatusExternal.CONFIRMED];

export const TopMerchants = ({ timeInterval }: TopMerchantsProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { customBorderRadius } = useCustomTheme();

  const { filteredMetrics, hasRequested } = useMetricMerchantViews({
    currency,
    timeInterval,
    loanStatusExternal,
  });

  const metricsAnalyzer = useMemo(() => new MerchantMetricAnalyzer(timeInterval, filteredMetrics), [filteredMetrics]);

  const metrics = metricsAnalyzer.scoreCardMetrics(loanStatusExternal[0]);

  const hasMetrics = metrics.filter((metric) => metric.totalPrincipalAmount.current > 0).length > 0;

  return (
    <Box flexGrow={1}>
      <Box mb={3}>
        <Font variant="h4" color="title">
          <strong>
            <Text tId="TOP_MERCHANTS_TITLE" />
          </strong>
        </Font>
        <Font variant="body2" color="textSecondary">
          <Text
            tId="TOP_MERCHANTS_DESCRIPTION"
            dynamicDataTexts={[(getTextIdTranslation(timeInterval.type) as string).toLocaleLowerCase()]}
          />
        </Font>
      </Box>
      <Box display={'flex'} flexDirection={'column'} gridGap={16}>
        {hasRequested === false &&
          [1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={`top-skeleton-${i}`}
              variant="rect"
              width={'100%'}
              height={66}
              style={{ borderRadius: customBorderRadius.paper }}
            />
          ))}
        {hasRequested && !hasMetrics && (
          <PendingDataWarning type={PendingDataType.NO_METRICS_INFORMATION} boxProps={{ height: 354 }} />
        )}
        {hasRequested &&
          hasMetrics &&
          metrics.map((metric, i) => (
            <MerchantScoreCard
              key={`top-${metric.businessName}`}
              index={i}
              currency={currency}
              timeInterval={timeInterval}
              metric={metric}
            />
          ))}
      </Box>
    </Box>
  );
};
